// Vendors
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Actions
import {
  selectMFAMethod,
  submitMFAChoice,
} from '../../../reducers/auth/authActions';

// Selectors
import {
  selectMFAOptions,
  selectSelectedMFA,
  selectUtmParams,
} from '../../../reducers/auth/authSelectors';
import { selectSelectedLanguage } from '../../../reducers/language/languageSelectors';

// Helpers
import { accountRecoveryUrl } from '../../../constants/appUrls';
import { buildUrl } from '../../../lib/utmHelpers';

// Styles
import './choose-mfa.scss';

// Components
import {
  TxButton,
  TxButtonMode,
  TxLoadingText,
  TxRadioGroup,
} from 'texkit-ui';
import { Trans } from '../../../components/Trans';

const ChooseMFA = () => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mfaOptions = useSelector(selectMFAOptions);
  const selectedMFA = useSelector(selectSelectedMFA);
  const language = useSelector(selectSelectedLanguage);
  const utmParams = useSelector(selectUtmParams);
  const { t } = useTranslation('auth');
  const langParam = `lang=${language}`;

  const mfaMethods = mfaOptions
    .filter(option => option && option.type && option.value)
    .map(option => ({
      id: option.type,
      labelPrefix: option.type === 'SMS' ? t('mfaOptionTextPrefix') : t('mfaOptionEmailPrefix'),
      maskedValue: option.value,
    }));

  const handleSubmit = async e => {
    e.preventDefault();

    if (!selectedMFA || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await dispatch(submitMFAChoice(selectedMFA));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelect = value => {
    dispatch(selectMFAMethod(value));
  };

  const handleOpenAccountRecovery = () => {
    window.open(buildUrl(`${accountRecoveryUrl}?${langParam}`, utmParams), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="step choose-mfa-step">
      <h1 className="step-title choose-mfa-title">
        <Trans file="auth" id="chooseMfaTitle" />
      </h1>

      <form className="choose-mfa-form" onSubmit={handleSubmit}>
        <TxRadioGroup
          className="choose-mfa-options"
          name="mfa-option"
          value={selectedMFA}
          onChange={e => handleSelect(e.target.value)}
          label={<span className="choose-mfa-sr-only">{t('chooseMfaOptionsAriaLabel')}</span>}
          options={mfaMethods.map(({ id, labelPrefix, maskedValue }) => ({
            value: id,
            label: (
              <span className={`choose-mfa-option-text ${selectedMFA === id ? 'choose-mfa-option-text-selected' : ''}`}>
                {labelPrefix}
                {' '}
                <strong className={`choose-mfa-option-masked-value ${(!selectedMFA || selectedMFA === id) ? 'choose-mfa-option-masked-value-bold' : ''}`}>{maskedValue}</strong>
              </span>
            ),
          }))}
        />

        <TxButton
          className="choose-mfa-btn"
          mode={TxButtonMode.Primary}
          type="submit"
          disabled={!selectedMFA || isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <TxLoadingText>
              <Trans file="auth" id="submitting" />
            </TxLoadingText>
          ) : (
            <Trans file="auth" id="sendCode" />
          )}
        </TxButton>

        <p className="choose-mfa-recovery-help">
          <strong><Trans file="auth" id="accountRecoveryPrefix" /></strong> {' '}
          <Trans file="auth" id="accountRecoveryMid" /> {' '}
          <TxButton
            type="button"
            className="choose-mfa-recovery-link"
            onClick={handleOpenAccountRecovery}
          >
            <Trans file="auth" id="deactivateAccount" />
          </TxButton>
          <Trans file="auth" id="accountRecoverySuffix" />
        </p>
      </form>
    </div>
  );
};

export default ChooseMFA;
